import React, { useEffect, useState } from 'react';
import { useController, ApplicationView } from '@aha-app/mvc';
import type AssistantViewController from '../AssistantView/AssistantViewController';
import styles from './ConversationPanel.module.css';
import classNames from 'classnames';
import MessageContent from './MessageContent';
import type AssistantToolCall from '../../../framework/models/AssistantToolCall';
import ThinkingIcon from '../ThinkingIcon/ThinkingIcon';
import { svgToIcon } from '../utils/iconUtils';
import { FROZEN_CHAT_ERROR } from 'components/Assistant/constants';
import Tool from '../../agent_tools/Tool';

const genericIcon = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='14'
    height='14'
    viewBox='0 0 14 14'
    fill='none'
  >
    <path
      d='M7 1.88672C7.90265 4.22595 9.76398 6.09537 12.1123 7C9.77336 7.90281 7.9035 9.76411 6.99902 12.1123C6.09605 9.77323 4.23521 7.90333 1.88672 6.99902C4.22608 6.09622 6.09553 4.23534 7 1.88672Z'
      stroke='currentColor'
      strokeWidth='1.2'
    />
  </svg>
);

interface ToolCallProps {
  toolCall: AssistantToolCall;
  isGrouped: boolean;
}

const ToolCall: React.FC<ToolCallProps> = ({
  toolCall,
  isGrouped = false,
}: ToolCallProps) => {
  const controller = useController<AssistantViewController>();
  const { currentArtifact } = controller.state;
  const hasPanel = toolCall.content?.panel;
  const panelOpen = currentArtifact === toolCall && hasPanel;
  const hasButtons = ['needs_approval', 'error'].includes(toolCall.status);
  const previewHasBody =
    toolCall.content?.preview?.body || (hasButtons && !hasPanel);
  const isComplete = toolCall.status === 'complete';
  const [previewCollapsed, setPreviewCollapsed] = useState(
    toolCall.content?.preview?.autoCollapse && isComplete
  );

  const isDevelopment = ['development', 'staging'].includes(
    process.env.NODE_ENV || 'unknown'
  );
  const availableStatuses = [
    'preparing',
    'processing',
    'complete',
    'error',
    'needs_approval',
    'rejected',
    'needs_frontend_content',
    'incomplete',
    'interrupted',
  ];

  const handleStatusChange = async (newStatus: string) => {
    await controller.actionUpdateToolCallStatus(toolCall, newStatus);
  };

  useEffect(() => {
    setPreviewCollapsed(toolCall.content?.preview?.autoCollapse && isComplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete]);

  const approveToolUse = (toolCall, data?: Record<string, unknown>) => {
    controller.actionApproveToolCall(toolCall, data);
    setPreviewCollapsed(true);
  };

  const getToolCallClassName = () => {
    switch (toolCall.status) {
      case 'preparing':
      case 'processing':
      case 'needs_frontend_content':
        return styles.toolCallProcessing;
      case 'complete':
        return styles.toolCallComplete;
      case 'error':
      case 'incomplete':
      case 'interrupted':
        return styles.toolCallError;
      case 'rejected':
        return styles.toolCallRejected;
      default:
        return '';
    }
  };

  const getStatusIcon = () => {
    if (hasPanel) {
      return (
        <aha-button kind='gray' size='mini'>
          {panelOpen ? 'Hide' : 'Show'}
        </aha-button>
      );
    }
    if (previewHasBody) {
      return (
        <i
          className={classNames('fa fa-regular', {
            'fa-chevron-down': previewHasBody && previewCollapsed,
            'fa-chevron-up': previewHasBody && !previewCollapsed,
          })}
        />
      );
    }

    switch (toolCall.status) {
      case 'preparing':
      case 'processing':
        return <i className='fa fa-regular fa-circle-notch fa-spin' />;
      case 'complete':
        return null;
      case 'error':
        return <i className='fa fa-regular fa-exclamation' />;
      case 'rejected':
        return <i className='fa fa-regular fa-ban' />;
      default:
        return null;
    }
  };

  const getToolIcon = () => {
    if (['preparing', 'processing'].includes(toolCall.status)) {
      return <ThinkingIcon />;
    } else {
      const icon = toolCall.icon || 'fa-circle-question';

      if (isGrouped) {
        return <div className={styles.toolCallIcon}>{genericIcon}</div>;

        // Check if the icon is an SVG string
      } else if (
        icon.trim().startsWith('<svg') &&
        icon.trim().endsWith('</svg>')
      ) {
        return <div className={styles.toolCallIcon}>{svgToIcon(icon)}</div>;
      } else {
        return (
          <div className={styles.toolCallIcon}>
            <i className={`fa fa-regular ${icon}`} />
          </div>
        );
      }
    }
  };

  const togglePreview = (e: React.MouseEvent) => {
    if (hasPanel) {
      e.stopPropagation();
      if (currentArtifact === toolCall) {
        controller.actionCloseArtifact();
      } else {
        controller.actionOpenArtifact(toolCall);
      }
    } else if (previewHasBody) {
      e.stopPropagation();
      setPreviewCollapsed(!previewCollapsed);
    }
  };

  if (!toolCall.content?.preview) {
    return null;
  }

  if (hasPanel && previewHasBody) {
    console.error('Tool should not have both a panel and a preview body');
  }

  const contentAction = Tool.onContentAction(toolCall, controller.agent);

  return (
    <div
      className={classNames(styles.toolCall, getToolCallClassName(), {
        [styles.toolCallOpen]: currentArtifact === toolCall,
        [styles.toolCallCollapsed]:
          (previewHasBody && previewCollapsed) || !previewHasBody,
        [styles.toolCallExpanded]: previewHasBody && !previewCollapsed,
        [styles.toolCallClickable]:
          (previewCollapsed && previewHasBody) || hasPanel,
        [styles.toolCallHasArtifact]: hasPanel,
        [styles.toolCallGrouped]: isGrouped,
      })}
      onClick={e => {
        (previewCollapsed || hasPanel) && togglePreview(e);
      }}
      data-tool-call-id={toolCall.id}
    >
      <div
        className={styles.toolCallHeader}
        onClick={e => !previewCollapsed && togglePreview(e)}
      >
        {getToolIcon()}
        <div
          className={styles.toolCallPreviewTitle}
          onClick={e => togglePreview(e)}
        >
          <MessageContent body={toolCall.content.preview.title} />
        </div>
        <div className={styles.toolCallDevGear}>
          {isDevelopment && (
            <aha-menu>
              <i
                slot='control'
                className='fa fa-regular fa-gear'
                style={{
                  fontSize: '12px',
                  cursor: 'pointer',
                  color: '#666',
                }}
                title='Change status (dev mode)'
              />
              <aha-menu-content>
                {availableStatuses.map(status => (
                  <aha-menu-item
                    key={status}
                    onClick={e => {
                      e.stopPropagation();
                      void handleStatusChange(status);
                    }}
                    style={{
                      fontWeight:
                        status === toolCall.status ? 'bold' : 'normal',
                    }}
                  >
                    {status}
                  </aha-menu-item>
                ))}
              </aha-menu-content>
            </aha-menu>
          )}
        </div>
        <div className={styles.toolCallStatusIcon}>{getStatusIcon()}</div>
      </div>
      {toolCall.content.preview.body && (
        <div className={classNames(styles.toolCallContent)}>
          <MessageContent
            body={toolCall.content.preview.body}
            contentType={toolCall.content.preview.contentType}
            approveAction={(data?: Record<string, unknown>) =>
              approveToolUse(toolCall, data)
            }
            rejectAction={() => controller.actionRejectToolCall(toolCall)}
            toolCallId={toolCall.id}
            contentAction={contentAction}
          />

          {toolCall.status === 'needs_approval' &&
            toolCall.content?.actionButton?.label !== 'SKIP' && (
              <>
                <div className={styles.toolCallApproval}>
                  <aha-button
                    kind='primary'
                    size='mini'
                    {...(controller.model.isChatFrozen
                      ? { disabled: true }
                      : {})}
                    onClick={e => approveToolUse(toolCall)}
                  >
                    {toolCall.content?.actionButton?.label || 'Approve'}
                  </aha-button>
                  <aha-button
                    kind='secondary'
                    size='mini'
                    {...(controller.model.isChatFrozen
                      ? { disabled: true }
                      : {})}
                    onClick={e => controller.actionRejectToolCall(toolCall)}
                  >
                    {toolCall.content?.rejectButton?.label || 'Ignore'}
                  </aha-button>
                </div>
                {controller.model.isChatFrozen && (
                  <aha-tooltip>{FROZEN_CHAT_ERROR}</aha-tooltip>
                )}
              </>
            )}
          {toolCall.status === 'error' && (
            <>
              <div className={styles.toolCallApproval}>
                <aha-button
                  kind='primary'
                  size='mini'
                  {...(controller.model.isChatFrozen ? { disabled: true } : {})}
                  onClick={() => approveToolUse(toolCall)}
                >
                  Try again
                </aha-button>
              </div>
              {controller.model.isChatFrozen && (
                <aha-tooltip>{FROZEN_CHAT_ERROR}</aha-tooltip>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ApplicationView(ToolCall);
